import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Room,
  RoomEvent,
  RemoteParticipant,
  RemoteTrackPublication,
  Track,
  SubscriptionError,
} from 'livekit-client';
import { useInterval } from '../../../hooks/useInterval';
import { useDispatch, useSelector } from "react-redux";
import { restoreCameraTrack } from '../store/livekitSlice/livekitSlice';
import {restoreCameraTrackBySid} from '../store/livekitSlice/livekitSlice';
const SYNC_INTERVAL_MS = 5000;
const MAX_RETRIES = 5;
const BASE_RETRY_DELAY_MS = 1000;

// --- Helper Functions ---

/**
 * Finds a RemoteTrackPublication by its SID across all participants.
 */
function findPublicationBySid(room: Room, sid: string): RemoteTrackPublication | undefined {
  for (const participant of room.remoteParticipants.values()) {
    const pub = participant.trackPublications.get(sid);
    if (pub) {
      return pub as RemoteTrackPublication;
    }
  }
  return undefined;
}

/**
 * Triage logic for subscription errors.
 * Classifies errors as 'unrecoverable' or 'retryable'.
 */
function classifySubscriptionError(error: SubscriptionError): 'unrecoverable' | 'retryable' {
  switch (error) {
    case SubscriptionError.SE_CODEC_UNSUPPORTED:
      // Codec not supported - this is a configuration issue and won't resolve with retries
      return 'unrecoverable';
    
    case SubscriptionError.SE_TRACK_NOTFOUND:
      // Track not found - this could be a race condition where the track
      // hasn't fully propagated yet, so it's worth retrying
      return 'retryable';
    
    case SubscriptionError.SE_UNKNOWN:
    default:
      // Unknown errors could be transient network issues, so retry
      return 'retryable';
  }
}

/**
 * Get all track publications from all remote participants.
 */
function getAllRemoteTrackPublications(room: Room): RemoteTrackPublication[] {
  const publications: RemoteTrackPublication[] = [];
  
  for (const participant of room.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      publications.push(publication as RemoteTrackPublication);
    }
  }
  
  return publications;
}

interface FailedTrackState {
  count: number;
  error?: SubscriptionError;
}

/**
 * A production-grade hook to manage manual track subscriptions with
 * error handling, retries, and self-healing reconciliation.
 * 
 * Assumes the Room is connected with `autoSubscribe: false`.
 *
 * @param room The LiveKit Room object.
 */
export const useTrackSubscription = (room: Room | undefined) => {
  const [failedPermanently, setFailedPermanently] = useState(new Set<string>());
  const [failedRetryable, setFailedRetryable] = useState(new Map<string, FailedTrackState>());
  const retryTimers = useRef(new Map<string, number>());
const { stopedCameraTracks } = useSelector(
  (state: { livekit: { stopedCameraTracks: { [email: string]: string } } }) => state.livekit
);
  const dispatch = useDispatch();


  // --- Phase 1: Event-Driven Subscription & Error Handling ---

  useEffect(() => {
    if (!room) return;

    // 1. Subscribe to new tracks as they are published
    const onTrackPublished = (
      publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ) => {
      // Check if this track is already on a failure list
      if (failedPermanently.has(publication.trackSid) || failedRetryable.has(publication.trackSid)) {
        return;
      }
      
      console.log(`🎯 Attempting to subscribe to new track: ${publication.trackSid}`);
      publication.setSubscribed(true);
    };

    // 2. Handle subscription failures with triage
    const onSubscriptionFailed = (
      trackSid: string,
      participant: RemoteParticipant,
      error?: SubscriptionError
    ) => {
      console.warn(`❌ Failed to subscribe to ${trackSid}`, error);
      
      // If no error provided, treat as retryable unknown error
      const errorType = error ?? SubscriptionError.SE_UNKNOWN;
      const classification = classifySubscriptionError(errorType);
      
      if (classification === 'unrecoverable') {
        setFailedPermanently(prev => new Set(prev).add(trackSid));
      } else {
        setFailedRetryable(prev => {
          const next = new Map(prev);
          const count = (next.get(trackSid)?.count || 0) + 1;
          next.set(trackSid, { count, error: errorType });
          return next;
        });
      }
    };

    // 3. Clean up retry state on success
    const onTrackSubscribed = (
      track: Track,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ) => {
      if (failedRetryable.has(publication.trackSid)) {
        console.log(`✅ Successfully subscribed to ${publication.trackSid}, clearing retry state`);
        setFailedRetryable(prev => {
          const next = new Map(prev);
          next.delete(publication.trackSid);
          return next;
        });
      }
    };

    // 4. State cleanup on track/participant departure
    const cleanupFailedState = (trackSid: string) => {
      setFailedPermanently(prev => {
        if (prev.has(trackSid)) {
          const next = new Set(prev);
          next.delete(trackSid);
          return next;
        }
        return prev;
      });
      setFailedRetryable(prev => {
        if (prev.has(trackSid)) {
          const next = new Map(prev);
          next.delete(trackSid);
          return next;
        }
        return prev;
      });
      // Clear any pending timers
      if (retryTimers.current.has(trackSid)) {
        clearTimeout(retryTimers.current.get(trackSid)!);
        retryTimers.current.delete(trackSid);
      }
    };
    
    const onTrackUnpublished = (publication: RemoteTrackPublication) => {
      console.log(`🗑️ Track unpublished: ${publication.trackSid}`);
      dispatch(restoreCameraTrackBySid(publication.trackSid));
      cleanupFailedState(publication.trackSid);
    };
    
    const onParticipantDisconnected = (participant: RemoteParticipant) => {
      console.log(`👋 Participant disconnected: ${participant.identity}`);
      participant.trackPublications.forEach(pub => {
        cleanupFailedState(pub.trackSid);
      });
    };

    // --- Register all listeners ---
    room.on(RoomEvent.TrackPublished, onTrackPublished);
    room.on(RoomEvent.TrackSubscriptionFailed, onSubscriptionFailed);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnpublished, onTrackUnpublished);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    
    // --- Phase 2: Subscribe to Existing Tracks on Join ---
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        const remotePub = publication as RemoteTrackPublication;
        if (!remotePub.isSubscribed && !failedPermanently.has(publication.trackSid) && !failedRetryable.has(publication.trackSid)) {
          console.log(`🎯 Attempting to subscribe to existing track: ${publication.trackSid}`);
          remotePub.setSubscribed(true);
        }
      });
    });

    // --- Cleanup all listeners ---
    return () => {
      room.off(RoomEvent.TrackPublished, onTrackPublished);
      room.off(RoomEvent.TrackSubscriptionFailed, onSubscriptionFailed);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.TrackUnpublished, onTrackUnpublished);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
      // Clear all running timers
      retryTimers.current.forEach(timer => clearTimeout(timer));
      retryTimers.current.clear();
    };
  }, [room, failedPermanently, failedRetryable]);

  // --- Phase 3: Resilient Retry Strategy (Exponential Backoff) ---
  
  useEffect(() => {
    if (!room) return;
    
    const currentTimers = retryTimers.current;

    for (const [trackSid, state] of failedRetryable.entries()) {
      // If a timer is already running for this SID, skip
      if (currentTimers.has(trackSid)) continue;

      if (state.count > MAX_RETRIES) {
        console.error(`🚨 Track ${trackSid} failed ${MAX_RETRIES} retries. Moving to permanent failure.`);
        setFailedRetryable(prev => {
          const next = new Map(prev);
          next.delete(trackSid);
          return next;
        });
        setFailedPermanently(prev => new Set(prev).add(trackSid));
        continue;
      }

      // Calculate delay with exponential backoff and jitter
      const delay = BASE_RETRY_DELAY_MS * Math.pow(2, state.count - 1) + (Math.random() * 1000);

      const timer = setTimeout(() => {
        const publication = findPublicationBySid(room, trackSid);
        
        if (publication && !publication.isSubscribed && !failedPermanently.has(trackSid)) {
          console.log(`🔄 Retrying subscription for ${trackSid} (Attempt ${state.count})`);
          publication.setSubscribed(true);
        }

        // Remove timer reference so it can be re-run if it fails again
        currentTimers.delete(trackSid);
      }, delay);
      
      currentTimers.set(trackSid, timer);
    }
    
    // Cleanup function to clear timers if dependencies change
    return () => {
      // Clear any timers that were just created
      for (const [trackSid, timer] of currentTimers.entries()) {
        if (failedRetryable.has(trackSid)) {
          clearTimeout(timer);
        }
      }
    };
  }, [room, failedRetryable, failedPermanently]);

  // --- Phase 4: Self-Healing Redundancy Sweeper ---

  const redundancyCheck = useCallback(() => {
    if (!room) return;

    const allPublications = getAllRemoteTrackPublications(room);

    for (const pub of allPublications) {
      const sid = pub.trackSid;
      if (Object.values(stopedCameraTracks).includes(sid)) {
        console.log(`⏸ Track ${sid} ignoré car volontairement stoppé`);
        continue;
      }
      if (
        !pub.isSubscribed &&
        !failedPermanently.has(sid) &&
        !failedRetryable.has(sid)
      ) {
        console.warn(`🔧 Found missed/unsubscribed track ${sid}, attempting subscription.`);
        pub.setSubscribed(true);
      }
    }
  }, [room, failedPermanently, failedRetryable, stopedCameraTracks]);

  useInterval(redundancyCheck, room ? SYNC_INTERVAL_MS : null);

const cleanupFailedState = useCallback((trackSid: string) => {
    // 1. Nettoyer les timers
    if (retryTimers.current.has(trackSid)) {
      clearTimeout(retryTimers.current.get(trackSid)!);
      retryTimers.current.delete(trackSid);
    }
    
    // 2. Nettoyer l'état permanent
    setFailedPermanently(prev => {
      if (prev.has(trackSid)) {
        const next = new Set(prev);
        next.delete(trackSid);
        return next;
      }
      return prev;
    });

    // 3. Nettoyer l'état de nouvelle tentative
    setFailedRetryable(prev => {
      if (prev.has(trackSid)) {
        const next = new Map(prev);
        next.delete(trackSid);
        return next;
      }
      return prev;
    });
  }, []); 

  const unsubscribeTracks = useCallback((trackSids: string[]) => {
    if (!room) return;
    
    // Vérification pour s'assurer que trackSids est un tableau valide
    if (!Array.isArray(trackSids) || trackSids.length === 0) {
        return;
    }

    console.log(`👋 Unsubscribing from tracks: ${trackSids.join(', ')}`);

    trackSids.forEach(trackSid => {
      const publication = findPublicationBySid(room, trackSid);
      
      if (publication) {
        if (publication.isSubscribed) {
          console.log(`🛑 Setting unsubscribed to false for track: ${trackSid}`);
          // C'est l'appel qui désabonne le client local de cette piste spécifique
          publication.setSubscribed(false); 
        }
        
        // Utilisation de la fonction centralisée pour nettoyer l'état
        cleanupFailedState(trackSid);
      } else {
        console.warn(`Track publication not found for SID: ${trackSid}.`);
      }
    });
  }, [room, cleanupFailedState]); // Dépend de room et de cleanupFailedState
  
  
useEffect(() => {
  const trackSids = Object.values(stopedCameraTracks);
  if (trackSids.length > 0) {
    unsubscribeTracks(trackSids);
  }
}, [stopedCameraTracks, unsubscribeTracks]);



  
  // Return state for debugging/UI, if needed
  return { failedPermanently, failedRetryable };
};